/**
 * Hardened auth token storage.
 *
 * Notes on security posture (explicit trade-off):
 *  - We intentionally keep tokens in localStorage (chosen by the user — option a).
 *  - We harden it by validating the JWT format on read, expiring tokens client-side,
 *    and refusing to store malformed strings (basic XSS injection guard).
 *  - DOMPurify is used elsewhere to sanitize rendered HTML so stored tokens can't
 *    be exfiltrated via rendered content paths.
 */

const TOKEN_KEY = 'authToken';

// Accept only JWT-like tokens: three base64url segments separated by dots.
const JWT_REGEX = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const safeBase64UrlDecode = (segment) => {
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4;
    const padStr = pad ? padded + '='.repeat(4 - pad) : padded;
    return atob(padStr);
  } catch {
    return null;
  }
};

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  if (!JWT_REGEX.test(token)) return null;
  const parts = token.split('.');
  const payloadJson = safeBase64UrlDecode(parts[1]);
  if (!payloadJson) return null;
  try {
    return JSON.parse(payloadJson);
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    // If we cannot verify expiry, treat as expired to be safe.
    return true;
  }
  // exp is in seconds
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec;
};

export const setAuthToken = (token) => {
  if (!token || typeof token !== 'string' || !JWT_REGEX.test(token)) {
    // Refuse to store anything that isn't a valid JWT shape.
    return false;
  }
  try {
    localStorage.setItem(TOKEN_KEY, token);
    return true;
  } catch (e) {
    console.error('Failed to persist auth token:', e);
    return false;
  }
};

export const getAuthToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || !JWT_REGEX.test(token)) {
      // Clean up tampered/malformed values.
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    if (isTokenExpired(token)) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return token;
  } catch (e) {
    console.error('Failed to read auth token:', e);
    return null;
  }
};

export const clearAuthToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    console.error('Failed to clear auth token:', e);
  }
};


// ============================================================
// Admin impersonation — preserves the admin's original token so they
// can exit the impersonated session and return to /admin.
// ============================================================
const ORIGINAL_TOKEN_KEY = 'originalAuthToken';

export const startImpersonation = (impersonationToken) => {
  if (!impersonationToken || !JWT_REGEX.test(impersonationToken)) return false;
  try {
    const current = localStorage.getItem(TOKEN_KEY);
    if (current) localStorage.setItem(ORIGINAL_TOKEN_KEY, current);
    localStorage.setItem(TOKEN_KEY, impersonationToken);
    return true;
  } catch (e) {
    console.error('Failed to start impersonation:', e);
    return false;
  }
};

export const exitImpersonation = () => {
  try {
    const original = localStorage.getItem(ORIGINAL_TOKEN_KEY);
    if (!original) {
      localStorage.removeItem(TOKEN_KEY);
      return false;
    }
    localStorage.setItem(TOKEN_KEY, original);
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    return true;
  } catch (e) {
    console.error('Failed to exit impersonation:', e);
    return false;
  }
};

export const isImpersonating = () => {
  const payload = decodeJwtPayload(localStorage.getItem(TOKEN_KEY));
  return !!(payload && payload.is_impersonating);
};

export const getImpersonationInfo = () => {
  const payload = decodeJwtPayload(localStorage.getItem(TOKEN_KEY));
  if (!payload || !payload.is_impersonating) return null;
  return {
    target_user_id: payload.sub,
    target_email: payload.email,
    impersonator_user_id: payload.impersonator_user_id,
    impersonator_email: payload.impersonator_email,
    expires_at: payload.exp ? new Date(payload.exp * 1000) : null,
  };
};
